import React, { useEffect, useState, useCallback } from 'react';
import { Row, Col, Button, Form } from 'react-bootstrap';
import { useSelector } from "react-redux"
import { toast } from 'react-toastify';
import Select from 'components/general/ViewportSelect';

export default function Config({ getConfig, onSaved = null, formId = 'meetings-config-form' }) {

  const { user: authUser } = useSelector((x) => x.auth);
  const url = process.env.REACT_APP_URL;
  const [theme, setTheme] = useState(document.body.getAttribute('data-theme') || 'light');

  const [form, setForm] = useState({
    dayOfWeek: '',
    frequency: '',
    time: '',
    defaultMeetingMode: 'inPerson',
    inPersonAddress: '',
    inPersonMapsLink: '',
    onlineMeetingLink: '',
    location: '',
    locationLink: '',
    title: '',
    defaultAgendaTemplate: '',
    meetingCardDisplayRole: 'Toastmaster'
  });
  const [agendaTemplates, setAgendaTemplates] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setTheme(document.body.getAttribute('data-theme') || 'light');
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    fetch(`${url}/meetingsConfig`, {
      headers: {
        'Authorization': `Bearer ${authUser.token}`,
      }
    })
      .then(res => {
        if (res.status === 404) {
          setNotFound(true);
          setLoading(false);
          return null;
        }
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then(data => {
        if (data) {
          setForm({
            ...data,
            defaultMeetingMode: data.defaultMeetingMode || (data.locationLink ? 'online' : 'inPerson'),
            inPersonAddress: data.inPersonAddress ?? data.location ?? '',
            inPersonMapsLink: data.inPersonMapsLink ?? '',
            onlineMeetingLink: data.onlineMeetingLink ?? '',
            defaultAgendaTemplate: data.defaultAgendaTemplate || '',
            meetingCardDisplayRole: data.meetingCardDisplayRole || (Array.isArray(data.meetingCardDisplayRoles) && data.meetingCardDisplayRoles.length > 0
              ? data.meetingCardDisplayRoles[0]
              : 'Toastmaster')
          });
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [url, authUser.token]);

  useEffect(() => {
    fetch(`${url}/agendatemplates`, {
      headers: {
        'Authorization': `Bearer ${authUser.token}`,
      }
    })
      .then(res => res.json())
      .then(data => {
        setAgendaTemplates(Array.isArray(data) ? data : []);
      })
      .catch(() => setAgendaTemplates([]));
  }, [url, authUser.token]);

  useEffect(() => {
    fetch(`${url}/meetingRoles`, {
      headers: {
        'Content-Type': 'application/json',
      }
    })
      .then(res => res.json())
      .then(data => {
        const roleNames = Array.isArray(data)
          ? [...new Set(data.map(role => String(role?.name || '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b))
          : [];
        setAvailableRoles(roleNames);
      })
      .catch(() => setAvailableRoles([]));
  }, [url]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        meetingCardDisplayRole: form.meetingCardDisplayRole || ''
      };
      delete payload.meetingCardDisplayRoles;

      const response = await fetch(`${url}/meetingsConfig`, {
        method: isCreating ? 'PUT' : 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authUser.token}`,
        },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        toast.success(
          isCreating ? 'Meetings configuration created!' : 'Meetings configuration updated!',
          { position: 'bottom-left' }
        );
        setIsCreating(false);
        setNotFound(false);
        await getConfig();
        if (onSaved) onSaved();
      } else {
        const errorResponse = await response.json();
        toast.error(`Failed! ${errorResponse.error || response.statusText}`, { position: 'bottom-left' });
      }
    } catch (error) {
      toast.error(`Error! ${error.message}`, { position: 'bottom-left' });
    }
  }, [form, url, isCreating, getConfig, authUser.token, onSaved]);

  const handleCreateClick = () => {
    setIsCreating(true);
    setForm({
      dayOfWeek: '',
      frequency: '',
      time: '',
      defaultMeetingMode: 'inPerson',
      inPersonAddress: '',
      inPersonMapsLink: '',
      onlineMeetingLink: '',
      location: '',
      locationLink: '',
      title: '',
      defaultAgendaTemplate: '',
      meetingCardDisplayRole: 'Toastmaster'
    });
  };

  const roleOptions = availableRoles.map(roleName => ({
    value: roleName,
    label: roleName
  }));

  const selectedRoleOption = roleOptions.find(option => option.value === form.meetingCardDisplayRole) || null;

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: theme === 'dark' ? '#23272b' : '#fff',
      color: theme === 'dark' ? '#fff' : '#222',
      borderColor: state.isFocused ? (theme === 'dark' ? '#FFD700' : '#004165') : (theme === 'dark' ? '#444' : '#ccc'),
      boxShadow: state.isFocused ? `0 0 0 2px ${theme === 'dark' ? '#FFD700' : '#004165'}` : 'none',
      minHeight: '44px',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: theme === 'dark' ? '#23272b' : '#fff',
      color: theme === 'dark' ? '#fff' : '#222',
      zIndex: 9999,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: theme === 'dark' ? '#fff' : '#222',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? (theme === 'dark' ? '#FFD700' : '#004165')
        : state.isFocused
          ? (theme === 'dark' ? 'rgba(255,215,0,0.08)' : 'rgba(0,65,101,0.08)')
          : (theme === 'dark' ? '#23272b' : '#fff'),
      color: state.isSelected
        ? (theme === 'dark' ? '#23272b' : '#fff')
        : (theme === 'dark' ? '#fff' : '#222'),
      fontWeight: state.isSelected ? 700 : 400,
    }),
    input: (provided) => ({
      ...provided,
      color: theme === 'dark' ? '#fff' : '#222',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: theme === 'dark' ? '#888' : '#888',
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: theme === 'dark' ? '#FFD700' : '#004165',
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      backgroundColor: theme === 'dark' ? '#FFD700' : '#004165',
    }),
  };

  if (loading) return <div>Loading...</div>;

  if (notFound && !isCreating) {
    return (
      <Button variant="primary" onClick={handleCreateClick}>
        <i className='bi bi-plus-circle'></i> Create Meetings Configuration
      </Button>
    );
  }

  return (
    <Form id={formId} data-bs-theme={'dark'} onSubmit={handleSubmit}>
      <Row>
        <Col xs={12}>
          <p>
            Configure the default settings for meetings. This will be used when creating new meetings, but all these settings can be overridden when creating or editing a specific meeting.
          </p>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col>
          <Form.Group className="mb-3">
            <Form.Label>Day of Week</Form.Label>
            <Form.Select
              name="dayOfWeek"
              value={form.dayOfWeek || ''}
              onChange={handleChange}
            >
              <option value="">Select day</option>
              <option value="Monday">Monday</option>
              <option value="Tuesday">Tuesday</option>
              <option value="Wednesday">Wednesday</option>
              <option value="Thursday">Thursday</option>
              <option value="Friday">Friday</option>
              <option value="Saturday">Saturday</option>
              <option value="Sunday">Sunday</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col>
          <Form.Group className="mb-3">
            <Form.Label>Frequency</Form.Label>
            <Form.Select
              name="frequency"
              value={form.frequency || ''}
              onChange={handleChange}
            >
              <option value="">Select frequency</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="bi-weekly">Bi-weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="half-yearly">Half-yearly</option>
              <option value="yearly">Yearly</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col>
          <Form.Group className="mb-3">
            <Form.Label>Time</Form.Label>
            <Form.Control
              type="text"
              name="time"
              value={form.time || ''}
              onChange={handleChange}
              placeholder="e.g. 18:00"
              maxLength={10}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col>
          <Form.Group className="mb-3">
            <Form.Label>Default Meeting Mode</Form.Label>
            <Form.Select
              name="defaultMeetingMode"
              value={form.defaultMeetingMode || 'inPerson'}
              onChange={handleChange}
            >
              <option value="inPerson">In Person</option>
              <option value="online">Online</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col>
          <Form.Group className="mb-3">
            <Form.Label>Default In-Person Address</Form.Label>
            <Form.Control
              type="text"
              name="inPersonAddress"
              value={form.inPersonAddress || ''}
              onChange={handleChange}
              placeholder="e.g. Main Hall"
              maxLength={100}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col>
          <Form.Group className="mb-3">
            <Form.Label>Default In-Person Maps Link</Form.Label>
            <Form.Control
              type="text"
              name="inPersonMapsLink"
              value={form.inPersonMapsLink || ''}
              onChange={handleChange}
              placeholder="e.g. https://maps.example.com"
              maxLength={200}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col>
          <Form.Group className="mb-3">
            <Form.Label>Default Online Meeting Link</Form.Label>
            <Form.Control
              type="text"
              name="onlineMeetingLink"
              value={form.onlineMeetingLink || ''}
              onChange={handleChange}
              placeholder="e.g. https://teams.microsoft.com/..."
              maxLength={200}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              name="title"
              value={form.title || ''}
              onChange={handleChange}
              placeholder="Default Meeting Title"
              maxLength={100}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col>
          <Form.Group className="mb-3">
            <Form.Label>Default Agenda Template</Form.Label>
            <Form.Select
              name="defaultAgendaTemplate"
              value={form.defaultAgendaTemplate || ''}
              onChange={handleChange}
            >
              <option value="">No default template</option>
              {agendaTemplates.map(template => (
                <option key={template._id} value={template._id}>{template.name}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col>
          <Form.Group className="mb-3">
            <Form.Label>Meeting Card Display Role</Form.Label>
            <Select
              options={roleOptions}
              className='formInput'
              value={selectedRoleOption}
              onChange={option => setForm(prev => ({
                ...prev,
                meetingCardDisplayRole: option ? option.value : ''
              }))}
              isClearable
              isSearchable
              styles={customStyles}
              theme={selectTheme => ({
                ...selectTheme,
                borderRadius: 4,
                colors: {
                  ...selectTheme.colors,
                  primary25: '#495057',
                  primary: '#00A261',
                  neutral0: theme === 'dark' ? '#23272b' : '#fff',
                  neutral80: theme === 'dark' ? '#fff' : '#222',
                },
              })}
              placeholder='Select a role'
            />
            <Form.Text>
              The selected role will be used for the displayed name and avatar on meeting cards.
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>
    </Form>
  );
};